import classNames from 'classnames';
import { Check, ZapIcon } from 'lucide-react';
import Link from 'next/link';
import { VariantTreeGetT } from '@/components/v2/product/hooks/types';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import { priceFormatSuperscript } from '@/lib/price';
import { priceFormater } from '@/settings/index';
import i18n from '@/settings/i18n';

const t = i18n.init('product');

// The upsell is specifically about reaching a 100% battery.
const UPGRADE_TARGET = '100';

const Toggle = ({
  upgradeOffer,
  baselineOffer,
}: {
  upgradeOffer: VariantTreeGetT;
  baselineOffer?: VariantTreeGetT;
}) => {
  const { productId, selectedVariant } = useProductPageContext();

  // No cheaper option to switch back to — the 100% battery is simply included.
  if (!baselineOffer) {
    return <span>{t`Included`}</span>;
  }

  const priceDifference = upgradeOffer.price - baselineOffer.price;
  const isUpgraded = selectedVariant?.skuId === upgradeOffer.skuId;

  // Toggling on selects the 100% SKU; toggling off returns to the cheapest one.
  const targetSkuId = isUpgraded ? baselineOffer.skuId : upgradeOffer.skuId;

  return (
    <div className="flex items-center gap-x-2">
      {priceDifference > 0 && (
        <span translate="no">
          +{priceFormatSuperscript(priceFormater.long(priceDifference))}
        </span>
      )}
      <Link
        href={`/collections/${encodeURIComponent(
          productId,
        )}?skuId=${encodeURIComponent(targetSkuId)}`}
        scroll={false}
        shallow
      >
        <button
          id="e2e-pdp-battery-upgrade"
          aria-label={t`Upgrade to 100% battery capacity`}
          aria-pressed={isUpgraded}
          className={classNames(
            'z-10 flex h-5 w-10 shrink-0 items-center justify-end rounded-full px-0.5 transition-all duration-150 ease-in-out focus:outline-none',
            {
              'bg-[#A5BCB6]': !isUpgraded,
              'bg-[#116A43]': isUpgraded,
            },
          )}
        >
          <div
            className={classNames(
              'h-4 w-4 rounded-full bg-white transition-all duration-150 ease-in-out',
              {
                'mr-1 -translate-x-full': !isUpgraded,
              },
            )}
          />
        </button>
      </Link>
    </div>
  );
};

const BatteryHealthUpgrade = () => {
  const { selectedVariant, variantTree } = useProductPageContext();

  const currentSelection = selectedVariant?.variants;

  // Return null if there is no current selection
  if (!currentSelection) {
    return null;
  }

  const batteryHealthOptions =
    variantTree.getBatteryHealthOptions(currentSelection);

  const upgradeOffer = batteryHealthOptions[UPGRADE_TARGET];

  // Only display if a selectable 100% battery health option is available.
  if (!upgradeOffer) {
    return null;
  }

  // Baseline = cheapest selectable battery-health option other than 100%
  const baselineOffer = Object.entries(batteryHealthOptions)
    .filter(([value]) => value !== UPGRADE_TARGET)
    .map(([, offer]) => offer)[0];

  const isFreeUpgrade = !baselineOffer;

  const badge = isFreeUpgrade
    ? {
        text: t`Free Upgrade`,
        icon: <Check className="h-3 w-3 text-[#128452]" strokeWidth={2.5} />,
      }
    : { text: t`Upgrade Available`, icon: null };

  return (
    <div className="mt-5 px-2">
      <div className="relative flex min-h-[60px] items-center justify-between gap-4 rounded-md border border-[#E0F0E9] bg-[linear-gradient(98deg,#F2FFF9_44.04%,#FFF_103.46%)] p-4 text-xs">
        <div className="absolute right-4 top-[-10px] flex h-5 items-center justify-center rounded-md bg-[#D6FFE2] px-2 text-xxs font-medium text-[#128452]">
          {badge.icon && <span className="mr-1">{badge.icon}</span>}
          {badge.text}
        </div>
        <div className="flex items-center gap-x-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#D6FFE2]">
            <ZapIcon className="h-4 w-4 fill-[#128452] text-[#128452]" />
          </div>
          <p>
            <span className="font-medium">{t`New battery upgrade`}</span>{' '}
            {t`with 100% capacity`}
          </p>
        </div>
        <Toggle upgradeOffer={upgradeOffer} baselineOffer={baselineOffer} />
      </div>
    </div>
  );
};

export default BatteryHealthUpgrade;
